'use strict';

/**
 * PlansheetChecklistProvider
 * --------------------------
 * Adapts the pict-section-checklist data seam (ChecklistDataProvider) to the plansheet ChecklistAPI.
 * One shared instance serves every checklist control on the page: a control is bound to a single
 * Checklist by its Context ({ OwnerType: 'Checklist', IDOwner: <IDChecklist> }), so every call reads
 * the id from the context and there is no per-instance state. The server owns tenancy and the
 * private/shared privacy check; this just maps rows to and from the neutral { List, Item } shapes.
 */

const libChecklist = require('pict-section-checklist');

class PlansheetChecklistProvider extends libChecklist.ChecklistDataProvider
{
	constructor(pPict)
	{
		super({});
		this.pict = pPict;
	}

	_api() { return this.pict.providers.ChecklistAPI; }
	_idFromContext(pContext) { return parseInt((pContext && pContext.IDOwner) || 0, 10) || 0; }

	_toItem(pRow)
	{
		return {
			Key: pRow.IDChecklistItem,
			ListKey: pRow.IDChecklist,
			ParentKey: pRow.IDParentChecklistItem ? pRow.IDParentChecklistItem : null,
			Title: pRow.Title || '',
			Done: !!pRow.Done,
			Sort: (typeof pRow.SortOrder === 'number') ? pRow.SortOrder : (parseFloat(pRow.SortOrder) || 0),
			Notes: pRow.Notes || '',
			Collapsed: !!pRow.Collapsed
		};
	}

	// One round trip for the whole control (the checklist + its items).
	loadChecklist(pContext)
	{
		let tmpID = this._idFromContext(pContext);
		if (!tmpID) { return Promise.resolve({ List: null, Items: [] }); }
		return this._api().get(tmpID).then((pResult) =>
		{
			let tmpChecklist = (pResult && pResult.Checklist) || null;
			let tmpList = tmpChecklist ? { Key: tmpChecklist.IDChecklist, OwnerType: 'Checklist', IDOwner: tmpChecklist.IDChecklist, Title: tmpChecklist.Title || '' } : null;
			let tmpItems = ((pResult && pResult.Items) || []).map((pRow) => this._toItem(pRow));
			return { List: tmpList, Items: tmpItems };
		});
	}

	getList(pContext) { return this.loadChecklist(pContext).then((pLoaded) => pLoaded.List); }

	listItems(pListKey) { return this._api().get(pListKey).then((pResult) => ((pResult && pResult.Items) || []).map((pRow) => this._toItem(pRow))); }

	updateList(pKey, pPatch)
	{
		let tmpTitle = (pPatch && pPatch.Title) || '';
		return this._api().rename(pKey, tmpTitle).then(() => ({ Key: pKey, Title: tmpTitle }));
	}

	createItem(pDraft)
	{
		let tmpListKey = pDraft.ListKey;
		return this._api().createItem(tmpListKey,
			{
				ParentKey: (pDraft.ParentKey != null && pDraft.ParentKey !== '') ? pDraft.ParentKey : 0,
				Title: pDraft.Title || '',
				Sort: (typeof pDraft.Sort === 'number') ? pDraft.Sort : 0
			})
			.then((pItem) => pItem ? this._toItem(pItem) : { Key: 'pending-' + Date.now(), ListKey: tmpListKey, ParentKey: pDraft.ParentKey || null, Title: pDraft.Title || '', Done: false, Sort: pDraft.Sort || 0 });
	}

	updateItem(pKey, pPatch)
	{
		let tmpServerPatch = {};
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Done')) { tmpServerPatch.Done = pPatch.Done; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Title')) { tmpServerPatch.Title = pPatch.Title; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Collapsed')) { tmpServerPatch.Collapsed = pPatch.Collapsed; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Notes')) { tmpServerPatch.Notes = pPatch.Notes; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'ParentKey')) { tmpServerPatch.ParentKey = (pPatch.ParentKey != null && pPatch.ParentKey !== '') ? pPatch.ParentKey : 0; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Sort')) { tmpServerPatch.Sort = pPatch.Sort; }
		return this._api().updateItem(pKey, tmpServerPatch).then(() => ({ Key: pKey }));
	}

	deleteItem(pKey) { return this._api().deleteItem(pKey).then(() => undefined); }
}

module.exports = PlansheetChecklistProvider;
